import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Modal,
} from "@shopify/polaris";
import type { HorseInventoryLevel} from "../../api_utils/requests";
import { useHorseInventoryLevels } from "../../api_utils/requests";
import { QuantityChangeForm } from "./QuantityChangeForm";

export function QuantityChangeModal({
  activatorText,
  horseVariantId,
  disabled,
  tracked,
  setErrorMessage,
}: {
  readonly activatorText: string;
  readonly horseVariantId: number;
  readonly disabled: boolean;
  readonly tracked: boolean;
  readonly setErrorMessage: (message: string) => void;
}): React.JSX.Element {
  const [modalActive, setModalActive] = useState(false);
  // The text of the button that opens the modal
  const [totalAvailableQuantity, setTotalAvailableQuantity] = useState<string>(activatorText);

  const [horseInventoryLevels, setHorseInventoryLevels] = useState<HorseInventoryLevel[]>([]);
  const { data: hils, refetch } = useHorseInventoryLevels(
    {
      horse_variant_id: horseVariantId,
    },
    {
      enabled: modalActive,
    },
  );
  useEffect(() => {
    setHorseInventoryLevels(hils);
  }, [hils]);

  const openModal = async (): Promise<void> => {
    await refetch();
    setModalActive(true);
  };

  const closeModal = useCallback((): void => {
    setModalActive(false);
  }, []);

  // Make sure that totalAvailableQuantity is updated when parent table re-renders
  useEffect(() => {
    setTotalAvailableQuantity(activatorText);
  }, [activatorText]);

  const [isDirty, setIsDirty] = useState(false);

  const activator = (
    <Button disabled={disabled} onClick={openModal} variant="plain">
      {totalAvailableQuantity}
    </Button>
  );

  const hilsSaveCallback = useRef<() => Promise<HorseInventoryLevel[]>>(() => Promise.resolve([] as HorseInventoryLevel[]));
  const hilsDiscardCallback = useRef<() => void>(() => null);

  const [isSaving, setIsSaving] = useState(false);
  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      const updatedHorseInventoryLevels = await hilsSaveCallback.current();
      setHorseInventoryLevels(updatedHorseInventoryLevels);
      shopify.toast.show("Quantity updated");
      closeModal();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      activator={activator}
      onClose={closeModal}
      open={modalActive}
      primaryAction={{
        content: "Save",
        onAction: handleSave,
        disabled: !isDirty || isSaving,
      }}
      size="large"
      title="Change quantity"
    >
      <Modal.Section>
        <QuantityChangeForm
          disabled={disabled}
          discardCallback={hilsDiscardCallback}
          horseInventoryLevels={horseInventoryLevels}
          horseVariantId={horseVariantId}
          saveCallback={hilsSaveCallback}
          setErrorMessage={setErrorMessage}
          setIsDirty={setIsDirty}
          setTotalAvailableQuantity={setTotalAvailableQuantity}
          tracked={tracked}
        />
      </Modal.Section>
    </Modal>
  );
}
